import ArrowBack from '@mui/icons-material/ArrowBack';
import DateRange from '@mui/icons-material/DateRange';
import PriorityHigh from '@mui/icons-material/PriorityHigh';
import { buttonBaseClasses, darken, lighten, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { forwardRef } from 'react';

import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';

interface DateScopeBannerProps {
  dateLabel: string;
  onAction: () => void;
  variant: 'auto-extended' | 'scoped-to-range';
}

const DateScopeBanner = forwardRef<HTMLDivElement, DateScopeBannerProps>(
  ({ dateLabel, onAction, variant }, ref) => {
    const theme = useTheme();
    const isAutoExtended = variant === 'auto-extended';
    const isDark = theme.palette.mode === 'dark';
    const dotColor = isDark ? theme.palette.info.light : theme.brandV2.colors.treadBlue;
    const bandColor = isDark
      ? darken(theme.palette.info.light, 0.7)
      : lighten(theme.palette.info.light, 0.9);
    const bandWashColor = isDark
      ? darken(theme.palette.info.light, 0.55)
      : lighten(theme.palette.info.light, 0.78);

    let leadLabel: string;
    let detailLabel: string | null;
    let actionLabel: string;

    switch (variant) {
      case 'auto-extended':
        leadLabel = t('dispatch.smart_search.no_results_for_dates', { dates: dateLabel });
        detailLabel = t('dispatch.smart_search.showing_all_dates');
        actionLabel = t('dispatch.smart_search.back_to_dates', { dates: dateLabel });
        break;
      case 'scoped-to-range':
        leadLabel = t('dispatch.smart_search.showing_results_for_dates', {
          dates: dateLabel,
        });
        detailLabel = null;
        actionLabel = t('dispatch.smart_search.show_all_dates');
        break;
      default:
        throw new Error(`Unsupported DateScopeBanner variant: ${variant}`);
    }

    return (
      <Box
        ref={ref}
        role="status"
        aria-live="polite"
        sx={{
          alignItems: 'center',
          bgcolor: isAutoExtended ? bandColor : theme.semantic.surfaceRaised,
          borderBottom: `solid 1px ${theme.brandV2.colors.treadGray7}`,
          display: 'flex',
          gap: 1,
          px: 2,
          py: 0.5,
          ...(isAutoExtended && {
            animation: 'dateScopeBandWash 600ms ease-out 1',
            '@keyframes dateScopeBandWash': {
              '0%': { backgroundColor: bandColor },
              '45%': { backgroundColor: bandWashColor },
              '100%': { backgroundColor: bandColor },
            },
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          }),
        }}
      >
        {isAutoExtended ? (
          <Box sx={{ flexShrink: 0, height: 20, position: 'relative', width: 20 }}>
            <Box
              aria-hidden
              sx={{
                bgcolor: dotColor,
                borderRadius: '50%',
                inset: 0,
                opacity: 0,
                position: 'absolute',
                animation: 'dateScopeDotPulse 3600ms ease-out 1',
                '@keyframes dateScopeDotPulse': {
                  '0%': { opacity: 0.55, transform: 'scale(1)' },
                  '27%': { opacity: 0, transform: 'scale(1.8)' },
                  '33%': { opacity: 0.55, transform: 'scale(1)' },
                  '60%': { opacity: 0, transform: 'scale(1.8)' },
                  '66%': { opacity: 0.55, transform: 'scale(1)' },
                  '94%': { opacity: 0, transform: 'scale(1.8)' },
                  '100%': { opacity: 0, transform: 'scale(1)' },
                },
                '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
              }}
            />
            <Box
              sx={{
                alignItems: 'center',
                bgcolor: dotColor,
                borderRadius: '50%',
                display: 'flex',
                height: 20,
                justifyContent: 'center',
                position: 'relative',
                width: 20,
              }}
            >
              <PriorityHigh
                sx={{
                  color: isDark ? theme.semantic.surface : theme.semantic.textInverse,
                  fontSize: '13px',
                }}
              />
            </Box>
          </Box>
        ) : (
          <DateRange sx={{ color: 'text.secondary', flexShrink: 0, fontSize: '18px' }} />
        )}

        <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>
          {detailLabel ? `${leadLabel} ${detailLabel}` : leadLabel}
        </Typography>

        <SmallButton
          onClick={onAction}
          variant="text"
          sx={{
            [`&.${buttonBaseClasses.root}`]: {
              color: theme.semantic.linkText,
              fontSize: '12px',
              fontWeight: 600,
            },
          }}
        >
          <Box
            component="span"
            sx={{
              alignItems: 'center',
              display: 'inline-flex',
              gap: 0.75,
              textDecoration: 'underline',
              textUnderlineOffset: '2px',
            }}
          >
            {isAutoExtended && <ArrowBack sx={{ fontSize: '14px' }} />}
            {actionLabel}
          </Box>
        </SmallButton>
      </Box>
    );
  },
);

DateScopeBanner.displayName = 'DateScopeBanner';

export default DateScopeBanner;
